export type CsvProductRow = {
  sku: string;
  fabric: string;
  quantity: number;
  leg: string;
};

/** Split a single CSV line into trimmed fields, honoring quotes and escaped ("") quotes. */
export const parseCsvLine = (line: string): string[] => {
  const result: string[] = [];
  let current = "";
  let isQuoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"' && nextChar === '"') {
      current += '"';
      index += 1;
    } else if (char === '"') {
      isQuoted = !isQuoted;
    } else if (char === "," && !isQuoted) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }

  result.push(current.trim());
  return result;
};

/**
 * Parse a products CSV into rows. Columns are matched by header name (case-insensitive,
 * order-independent): `sku` (required), `fabric` (aliases: selectedfabric, selected_fabric,
 * material), `quantity`, and `leg` (all optional). Each row keeps its own fabric and leg, so
 * a single file may mix fabrics. Quantity defaults to 1 and is floored; invalid/<=0 → 1.
 */
export const parseProductsCsv = (text: string): CsvProductRow[] => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error("CSV must contain headers and at least one product row.");
  }

  const headers = parseCsvLine(lines[0]).map((header) => header.toLowerCase());
  const skuIndex = headers.indexOf("sku");
  const fabricIndex = ["fabric", "selectedfabric", "selected_fabric", "material"].reduce(
    (foundIndex, header) => (foundIndex >= 0 ? foundIndex : headers.indexOf(header)),
    -1
  );
  const quantityIndex = headers.indexOf("quantity");
  const legIndex = headers.indexOf("leg");

  if (skuIndex < 0) {
    throw new Error('CSV must contain a "sku" column.');
  }

  return lines.slice(1).map((line, index) => {
    const values = parseCsvLine(line);
    const sku = values[skuIndex]?.trim() ?? "";
    const fabric = fabricIndex >= 0 ? values[fabricIndex]?.trim() ?? "" : "";
    const leg = legIndex >= 0 ? values[legIndex]?.trim() ?? "" : "";
    const rawQuantity = quantityIndex >= 0 ? Number(values[quantityIndex]) : 1;
    const quantity = Number.isFinite(rawQuantity) && rawQuantity > 0 ? Math.floor(rawQuantity) : 1;

    if (!sku) {
      throw new Error(`CSV row ${index + 2} has empty SKU.`);
    }

    return {
      sku,
      fabric,
      quantity,
      leg,
    };
  });
};
